import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { isNull } from 'lodash/fp';
import classnames from 'classnames';

import styles from './DocumentName.module.scss';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { saveAgreementTypeHierarchyStarted, AgreementTypeHierarchy, updateAgreementHierarchy, getCurrentAgreementHierarchy, getCanSaveAgreementHierarchy, resetAgreementHierarchy, setAgreementRelationshipId, getSelectedAgreementTypeId, getIsLoadingAgreementHierarchy, fetchAgreementTypeHierarchyStarted, AgreementHierarchyRank, AgreementTypeDB } from './store';
import { Button } from '../../shared/button/Button';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { DeleteButton } from '../../shared/button/DeleteButton';
import { Spinner } from '../../shared/spinner/Spinner';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { UnsavedChangesModal } from '../../shared/modal/UnsavedChangesModal';

const NAV_BAR = 100;
const BUTTONS = 50;
const TITLE = 40;
const MARGINS = 80;
const AVAILABLE_OPTIONS = 160;
const HEIGHT_OFFSET = NAV_BAR + BUTTONS + TITLE + MARGINS + AVAILABLE_OPTIONS;

const { fixedWhite, french, lightGrey, frenchLight, primary } = styles;

interface HierarchyLevelProps {
    configHeight: number;
    verticalLineHeight: number;
    agreementTypes: AgreementTypeDB[];
    showParentHorizontal: boolean;
    showChildHorizontal: boolean;
    hasParents: boolean;
    hasChildren: boolean;
    isActiveTile?: boolean;
    removeChild?: (agreementTypeId: number, name: string) => void;
    backgroundColor?: string;
}

const HierarchyLevel: React.FC<HierarchyLevelProps> = ({ configHeight, agreementTypes, verticalLineHeight, showParentHorizontal, showChildHorizontal, hasParents, hasChildren, isActiveTile = false, removeChild, backgroundColor = lightGrey }) => {
    const color = backgroundColor === lightGrey ? primary : fixedWhite;
    return (
        <div className={styles.config} style={{ height: `${configHeight}px` }}>
            <Scrollable width='calc(100% + 2px)' autoCenterOnMount>
                <div className={styles.scrollableAgreementsWrapper} style={{ height: `${configHeight}px` }}>
                    {agreementTypes.map(({ name, agreementTypeId }, index) => (
                        <div className={styles.agreementTileWrapper} key={agreementTypeId}>
                            {showChildHorizontal && <div style={{ top: 0 }} className={classnames(styles.horizontalJoiningLine, { [styles.firstHorizontalJoiningLine]: index === 0, [styles.lastHorizontalJoiningLine]: index === agreementTypes.length - 1 })} />}
                            {hasParents && <div className={styles.verticalLineToParent} style={{ height: `${verticalLineHeight}px` }} />}
                            <div className={classnames(styles.agreementTile, { [styles.activeAgreementTile]: isActiveTile })} style={{ backgroundColor, color }}>
                                {removeChild && <div className={styles.deleteWrapper}>
                                    <DeleteButton onClick={() => removeChild(agreementTypeId, name)} fontSize={15} />
                                </div>}
                                <div className={styles.label}>{name}</div>
                            </div>
                            {hasChildren && <div className={styles.verticalLineToChild} style={{ height: `${verticalLineHeight}px` }} />}
                            {showParentHorizontal && <div style={{ bottom: 0 }} className={classnames(styles.horizontalJoiningLine, { [styles.firstHorizontalJoiningLine]: index === 0, [styles.lastHorizontalJoiningLine]: index === agreementTypes.length - 1 })} />}
                        </div>
                    ))}
                </div>
            </Scrollable>
        </div>
    );
};

export interface AgreementTypeFamilyTreeRouteParams {
    agreementTypeId: string | undefined;
}

export const AgreementTypeFamilyTree: React.FC<AgreementTypeFamilyTreeRouteParams> = ({ agreementTypeId }) => {

    const [, screenHeight] = useWindowResize();

    const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const currentAgreementHierarchy = useAppSelector(getCurrentAgreementHierarchy);
    const canSaveAgreementHierarchy = useAppSelector(getCanSaveAgreementHierarchy);
    const selectedAgreementTypeId = useAppSelector(getSelectedAgreementTypeId);
    const isFetchingHierarchy = useAppSelector(getIsLoadingAgreementHierarchy);

    useEffect(() => {
        if (isNull(selectedAgreementTypeId) && agreementTypeId) {
            dispatch(setAgreementRelationshipId(parseInt(agreementTypeId)));
            dispatch(fetchAgreementTypeHierarchyStarted(parseInt(agreementTypeId)));
        }
    }, [agreementTypeId, selectedAgreementTypeId, dispatch]);

    const backToTable = useCallback(() => {
        dispatch(resetAgreementHierarchy());
        dispatch(setAgreementRelationshipId(null));
    }, [dispatch]);

    const back = useCallback(() => canSaveAgreementHierarchy ? setConfirmationModalOpen(true) : backToTable(), [canSaveAgreementHierarchy, backToTable]);

    const saveAgreementHierarchy = useCallback(() => dispatch(saveAgreementTypeHierarchyStarted()), [dispatch]);

    const updateHierarchy = useCallback((agreementTypeId: number, name: string, filterFromRank: AgreementHierarchyRank, addToRank: AgreementHierarchyRank) => {
        const filteredRank = currentAgreementHierarchy[filterFromRank].filter(agreement => agreement.agreementTypeId !== agreementTypeId).sort((a, b) => a.agreementTypeId - b.agreementTypeId);
        const updatedRank = [...currentAgreementHierarchy[addToRank], { agreementTypeId, name }].sort((a, b) => a.agreementTypeId - b.agreementTypeId);
        const updatedHierarchy: AgreementTypeHierarchy = { ...currentAgreementHierarchy, [filterFromRank]: filteredRank, [addToRank]: updatedRank };
        dispatch(updateAgreementHierarchy(updatedHierarchy));
    }, [dispatch, currentAgreementHierarchy]);

    const addChild = useCallback((agreementTypeId: number, name: string) => updateHierarchy(agreementTypeId, name, AgreementHierarchyRank.AVAILABLE_AGREEMENTS, AgreementHierarchyRank.CHILDREN), [updateHierarchy]);
    const removeChild = useCallback((agreementTypeId: number, name: string) => updateHierarchy(agreementTypeId, name, AgreementHierarchyRank.CHILDREN, AgreementHierarchyRank.AVAILABLE_AGREEMENTS), [updateHierarchy]);

    const parentsOfAgreement = useMemo(() => currentAgreementHierarchy[AgreementHierarchyRank.PARENTS], [currentAgreementHierarchy]);
    const selectedAgreement = useMemo(() => currentAgreementHierarchy[AgreementHierarchyRank.SELECTED_AGREEMENT], [currentAgreementHierarchy]);
    const childAgreements = useMemo(() => currentAgreementHierarchy[AgreementHierarchyRank.CHILDREN], [currentAgreementHierarchy]);
    const availableAgreements = useMemo(() => currentAgreementHierarchy[AgreementHierarchyRank.AVAILABLE_AGREEMENTS], [currentAgreementHierarchy]);

    const hasChildren = useMemo(() => childAgreements.length > 0, [childAgreements]);
    const hasParents = useMemo(() => parentsOfAgreement.length > 0, [parentsOfAgreement]);
    const showParentHorizontal = useMemo(() => parentsOfAgreement.length > 1, [parentsOfAgreement]);
    const showChildHorizontal = useMemo(() => childAgreements.length > 1, [childAgreements]);
    const familyTreeWrapperHeight = useMemo(() => screenHeight - HEIGHT_OFFSET, [screenHeight]);
    const configHeight = useMemo(() => familyTreeWrapperHeight / (hasParents ? 3 : 2), [familyTreeWrapperHeight, hasParents]);
    const verticalLineHeight = useMemo(() => (configHeight - 132) / 2, [configHeight]);

    return (
        <div className={styles.agreementHierarchyWrapper}>
            <div className={styles.titleWrapper}>
                <div className={styles.title}>Agreement Family Tree</div>
            </div>
            <div className={styles.configWrapper}>
                {isFetchingHierarchy ? <Spinner /> :
                    <>
                        <div className={styles.familyTreeWrapper} style={{ height: `${familyTreeWrapperHeight}px` }}>
                            {hasParents &&
                                <HierarchyLevel
                                    configHeight={configHeight}
                                    agreementTypes={parentsOfAgreement}
                                    verticalLineHeight={verticalLineHeight}
                                    showParentHorizontal={showParentHorizontal}
                                    showChildHorizontal={false}
                                    hasParents={false}
                                    hasChildren={true}
                                    backgroundColor={frenchLight}
                                />
                            }
                            <HierarchyLevel
                                configHeight={configHeight}
                                agreementTypes={selectedAgreement}
                                verticalLineHeight={verticalLineHeight}
                                showParentHorizontal={false}
                                showChildHorizontal={false}
                                hasParents={hasParents}
                                hasChildren={hasChildren}
                                backgroundColor={french}
                            />
                            <HierarchyLevel
                                configHeight={configHeight}
                                agreementTypes={childAgreements}
                                verticalLineHeight={verticalLineHeight}
                                showParentHorizontal={false}
                                showChildHorizontal={showChildHorizontal}
                                hasParents={true}
                                hasChildren={false}
                                removeChild={removeChild}
                            />
                        </div>
                        <div className={styles.availableAgreements}>
                            {availableAgreements.length ?
                                <Scrollable>
                                    <div className={styles.scrollableAgreementsWrapper} style={{ height: '158px' }}>
                                        {availableAgreements.map(({ name, agreementTypeId }) => (
                                            <div className={styles.agreementTileWrapper} key={agreementTypeId}>
                                                <div className={classnames(styles.agreementTile, { [styles.activeAgreementTile]: true })} onClick={() => addChild(agreementTypeId, name)}>{name}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Scrollable>
                                :
                                <div className={styles.placeholderWrapper}>
                                    <div className={styles.placeholder}>No agreement types available</div>
                                </div>
                            }
                        </div>
                    </>
                }
            </div>
            <div className={styles.buttonWrapper}>
                <Button label='Back' onClick={back} />
                <Button label='Save' onClick={saveAgreementHierarchy} disabled={!canSaveAgreementHierarchy} />
            </div>
            <UnsavedChangesModal
                isOpen={confirmationModalOpen}
                closeModal={() => setConfirmationModalOpen(false)}
                confirm={backToTable}
            />
        </div>
    );
};
